import * as S from './styles'
import { useEffect, useState, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { asyncGetShipmentListByOvNumber } from 'store/OVContext/ovSlice'
import { useDispatch, useSelector } from 'store/store'
import {
  DSInputType,
  HeadItemType,
  Input,
  NotFound,
  Pagination,
  Select,
  Table,
  Typography
} from 'components'
import { Skeleton } from '@mui/material'
import { formatToPTBRNumber, numberToDecimalString } from 'commons/utils/number'
import { ShipmentByOv } from 'store/OVContext/ovContextTypes'
import useDebounce from 'commons/customHooks/useDebounce'

const ShipmentTab = () => {
  const { ovNumber } = useParams()
  const pageSize = 10

  const dispatch = useDispatch()
  const { shipmentInfo, shipmentIsLoading: loading } = useSelector((state) => state.ov)

  const [shipmentList, setShipmentList] = useState<ShipmentByOv[]>([])
  const [searchValue, setSearchValue] = useState('')
  const [shipmentsTabView, setShipmentsTabView] = useState('doc')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const searchRef = useRef<HTMLInputElement>(null)

  const getShipments = (page?: number, search?: string, view?: string) => {
    const pageQuery = page || currentPage
    const viewQuery = view || shipmentsTabView
    dispatch(
      asyncGetShipmentListByOvNumber(`${ovNumber}`, {
        currentPage: pageQuery - 1,
        pageSize,
        searchValue: search || '',
        selectedView: viewQuery
      })
    )
    setCurrentPage(pageQuery)
  }

  useEffect(() => {
    if (shipmentInfo.DocumentoRemessa?.length > 0) {
      setShipmentList(shipmentInfo.DocumentoRemessa)
      const qtdOfRows = shipmentInfo.RowsCount
      const amountOfPages = Math.ceil(qtdOfRows / pageSize)
      setTotalPages(amountOfPages)
      setCurrentPage((currentPg) =>
        currentPg > amountOfPages ? amountOfPages : currentPg
      )
    } else {
      setTotalPages(0)
      setCurrentPage(1)
      setShipmentList([])
    }
  }, [shipmentInfo])

  useEffect(() => {
    if (!loading) {
      searchRef.current?.focus()
    }
  }, [loading])

  useEffect(() => {
    getShipments(1)
  }, [ovNumber])

  const debounceChange = useDebounce(getShipments, 1000)

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLocaleUpperCase()
    setSearchValue(value)
    debounceChange(1, value)
  }

  const renderShipmentsSkeleton = () => {
    const tableData = []

    for (let i = 0; i <= 2; i++) {
      tableData.push({
        id: `skeleton-${i}`,
        content: Array(getShipmentsHead().length)
          .fill('')
          .map((_, index) => <Skeleton key={index} width={100} height={20} />)
      })
    }

    return (
      <Table
        hasCheckbox={false}
        headData={getShipmentsHead()}
        bodyData={tableData}
        rowHeight={32}
        distributeWidth={false}
      />
    )
  }

  const renderSummary = () => {
    return (
      <S.SummaryWrapper>
        <Pagination
          currentPage={currentPage}
          setCurrentPage={(value: number) => getShipments(value, searchValue)}
          numberOfPages={totalPages}
          numberOfItems={shipmentInfo.RowsCount}
          itemsPerPage={pageSize}
        />
        <S.SummaryRow>
          <S.SummaryItem>
            <Typography design="hav-default-02-text">Qtd. total</Typography>
            <span>
              {numberToDecimalString(shipmentInfo.TotalRemessa.QuantidadeTotal)}
            </span>
          </S.SummaryItem>
          <S.SummaryItem>
            <Typography design="hav-default-02-text">Valor total</Typography>
            <span>{formatToPTBRNumber(shipmentInfo.TotalRemessa.ValorTotal)}</span>
          </S.SummaryItem>
        </S.SummaryRow>
      </S.SummaryWrapper>
    )
  }

  const getShipmentSizes = (): HeadItemType[] => {
    const sizes: string[] = []

    if (!loading && shipmentList && shipmentList.length) {
      shipmentList.forEach((item) => {
        if (item.TamanhosRemessa.length) {
          item.TamanhosRemessa.map((size) => {
            if (!sizes.includes(size.CodigoTamanho)) {
              sizes.push(size.CodigoTamanho)
            }
          })
        }
      })

      sizes.sort()
    }

    return sizes.map((size) => ({
      label: size,
      columnName: size
    }))
  }

  const getShipmentData = () => {
    const tableData = []
    if (shipmentList) {
      const materialsData = shipmentList.map((item) => {
        const sizesRow = getShipmentSizes().map((size) => {
          const foundSize = item.TamanhosRemessa.find(
            (itemSize) => itemSize.CodigoTamanho === size.label
          )

          return foundSize ? foundSize.QuantidadeItensPares : <S.EmptySize>0</S.EmptySize>
        })

        const content: (number | string | JSX.Element)[] = [
          item.Documento,
          item.CodigoMaterial,
          item.CodigoCor,
          item.DescricaoCor,
          item.QuantidadeTotalItem,
          numberToDecimalString(item.ValorTotalItem)
        ]

        if (shipmentsTabView === 'doc') {
          content.push(...sizesRow)
        } else {
          content.splice(4, 0, item.TamanhosRemessa[0].CodigoTamanho)
          if (!loading) content.splice(5, 0, <S.HeaderField width="100px" />)
        }

        return {
          id: `${item.CodigoMaterial} + ${item.DescricaoCor}`,
          content
        }
      })
      tableData.push(...materialsData)
    }
    return tableData
  }

  const getShipmentsHead = () => {
    const tableHead: HeadItemType[] = [
      { label: 'Documento', columnName: 'Documento' },
      { label: 'Material', columnName: 'Material' },
      { label: 'Cor', columnName: 'Cor' },
      { label: 'Descrição', columnName: 'DescricaoCor' },
      { label: 'Qtd.', columnName: 'Qtd' },
      { label: 'Valor (R$)', columnName: 'Valor' }
    ]

    if (shipmentsTabView === 'doc') {
      const sizes = getShipmentSizes()
      tableHead.push(...sizes)
    } else {
      tableHead.splice(4, 0, { label: 'Tamanho', columnName: 'Tamanho' })
      if (!loading) tableHead.splice(5, 0, { label: '', columnName: '' })
    }

    return tableHead
  }

  const handleTableViewChange = (value: string) => {
    setShipmentsTabView(value)
    setSearchValue('')
    getShipments(1, '', value)
  }

  const tableViewOptions = [
    { value: 'doc', title: 'Documento' },
    { value: 'sku', title: 'SKU' }
  ]

  return (
    <S.Wrapper>
      <S.Header>
        <S.HeaderField width={shipmentsTabView === 'doc' ? '132px' : '83px'}>
          <Select
            name="TableView"
            value={shipmentsTabView}
            options={tableViewOptions}
            onChange={(e) => {
              const selectedOption = e.target.value
              handleTableViewChange(selectedOption as string)
            }}
            disabled={loading}
            isMenu
          />
        </S.HeaderField>
        <S.HeaderField width="270px">
          <Input
            addorment
            design={DSInputType.SEARCH_INPUT}
            value={searchValue}
            onChange={handleSearch}
            InputProps={{ placeholder: 'Buscar material em remessa' }}
            disabled={loading}
            ref={searchRef}
          />
        </S.HeaderField>
      </S.Header>
      {loading ? (
        renderShipmentsSkeleton()
      ) : !shipmentList.length ? (
        <NotFound
          title="Nenhuma remessa encontrada"
          description="As informações de remessa da Ordem de Venda serão listadas aqui."
        />
      ) : (
        <>
          <S.TableWrapper>
            <Table
              hasCheckbox={false}
              headData={getShipmentsHead()}
              bodyData={getShipmentData()}
              rowHeight={32}
              fixedFirstCol
            />
          </S.TableWrapper>
          {renderSummary()}
        </>
      )}
    </S.Wrapper>
  )
}

export default ShipmentTab
